import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { DOCUMENT_EVIDENCE_SOURCE_URL_LIMIT } from "@repo/shared/document-evidence-link";
import { useConvex } from "convex/react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildSourceUrlKey,
  chunkForRequest,
  type EvidenceSourceRequest,
  parseSourceUrlKey,
} from "./evidence-citation-rows";

/**
 * How long a minted source URL is reused before it is minted again. Storage URLs are signed
 * with an expiry, so a keep-alive session left open for hours must re-mint on the next open
 * rather than hand out a dead link.
 */
const EVIDENCE_URL_REFRESH_MS = 10 * 60 * 1000;

/**
 * Openable URLs for the sources a reader has open, minted on demand and cached per cited
 * source — the artifact *and* the revision that cites it.
 *
 * The revision is part of the identity because it selects the bytes: a revision was parsed from
 * one immutable source version, and once a document's source can be replaced, two revisions of
 * one artifact open two different files. Keying on the artifact alone would hand the second
 * citation the first one's link.
 *
 * Signing happens here rather than inside the long-lived `present` subscription, which keeps
 * URLs scoped to real intent. The request ledger makes a re-run within the refresh window a
 * no-op, so an unstable caller identity cannot spin this into a fetch loop; entries age out
 * so a long-lived session re-mints instead of handing out an expired link.
 *
 * A source that has not answered yet is absent from the map, which is what a caller reads
 * as "no action yet" — distinct from a stored `null`, which says the source has no
 * openable file at all.
 */
export function useEvidenceSourceUrls(
  projectId: Id<"projects"> | null,
  wantedSources: readonly EvidenceSourceRequest[]
): ReadonlyMap<string, string | null> {
  const convex = useConvex();
  const [sourceUrls, setSourceUrls] = useState<ReadonlyMap<string, string | null>>(() => new Map());
  const requestedAtBySourceKey = useRef<Map<string, number>>(new Map());
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // A string, not the array: the caller rebuilds its list on every render, and depending on
  // its identity would re-run this effect forever. Newlines separate entries because the key
  // itself is pipe-joined.
  const wantedKey = useMemo(
    () =>
      [
        ...new Set(
          wantedSources
            .filter((source) => source.artifactId)
            .map((source) => buildSourceUrlKey(source))
        ),
      ].join("\n"),
    [wantedSources]
  );

  useEffect(() => {
    if (!projectId) return;
    const now = Date.now();
    const sourceKeys = wantedKey.split("\n").filter((key) => {
      if (!key) return false;
      const requestedAt = requestedAtBySourceKey.current.get(key);
      return requestedAt === undefined || now - requestedAt > EVIDENCE_URL_REFRESH_MS;
    });
    if (sourceKeys.length === 0) return;
    for (const key of sourceKeys) requestedAtBySourceKey.current.set(key, now);

    // Deliberately not cancelled on dependency change: the ledger already makes a re-run a
    // no-op, so a re-render between issuing the query and its answer must not discard it.
    for (const batch of chunkForRequest(sourceKeys, DOCUMENT_EVIDENCE_SOURCE_URL_LIMIT)) {
      void Promise.resolve(
        // An action, not a query: a cited recording's original lives in media storage and its
        // link is a presigned GET, which only an action can sign.
        convex.action(api.documentEvidence.resolveOpenableSourceUrls, {
          projectId,
          sources: batch.map((key) => {
            const source = parseSourceUrlKey(key);
            return {
              artifactId: source.artifactId as Id<"artifacts">,
              ...(source.processingRevisionId
                ? {
                    processingRevisionId:
                      source.processingRevisionId as Id<"artifactProcessingRevisions">,
                  }
                : {}),
            };
          }),
        })
      )
        .then((results) => {
          if (!isMounted.current || !Array.isArray(results)) return;
          setSourceUrls((previous) => {
            const next = new Map(previous);
            for (const result of results) next.set(buildSourceUrlKey(result), result.url);
            return next;
          });
        })
        .catch(() => {
          // Losing the lookup costs only the action; the name, location and quote all still
          // render. Allow a retry on the next open.
          for (const key of batch) requestedAtBySourceKey.current.delete(key);
        });
    }
  }, [convex, projectId, wantedKey]);

  return sourceUrls;
}
